/**
 * Configuration Migration Admin Page
 *
 * Provides UI for exporting and importing organization configuration
 * across environments (dev → staging → production).
 *
 * Features:
 * - Export configuration from current environment
 * - Import configuration from file or another environment
 * - Preview changes before import
 * - Cross-environment transfer with credential management
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import {
  Download,
  Upload,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  FileJson,
  Server,
  ArrowRight,
  Eye,
  Play,
  Settings,
  Building2,
  Shield,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  X,
  Copy,
  Check
} from 'lucide-react';

// Types
interface ConfigurationScope {
  key: string;
  label: string;
  description: string;
  selected: boolean;
}

interface Organization {
  id: number;
  name: string;
}

interface Environment {
  key: string;
  name: string;
  url: string;
  connected: boolean;
  token?: string;
}

interface ImportCounts {
  create: number;
  update: number;
  skip: number;
  error: number;
}

interface ImportWarning {
  table: string;
  record_ref: string;
  message: string;
  severity: 'info' | 'warning' | 'error';
}

interface ImportPreview {
  preview_id: string;
  target_organization_id: number;
  target_organization_name: string;
  merge_strategy: string;
  billing_activities?: ImportCounts;
  place_of_service?: ImportCounts;
  diagnosis_codes?: ImportCounts;
  billing_dependencies?: ImportCounts;
  field_categories?: ImportCounts;
  custom_fields?: ImportCounts;
  field_configurations?: ImportCounts;
  programs?: ImportCounts;
  roles?: ImportCounts;
  warnings: ImportWarning[];
  errors: string[];
  ready_to_import: boolean;
}

interface ExportPackage {
  metadata: {
    version: string;
    created_at: string;
    source_environment: string;
    organization_id: number;
    organization_name: string;
    export_scope: string[];
  };
  billing?: any;
  member_profile?: any;
  programs?: any;
  roles?: any;
  workflows?: any;
}

const DEFAULT_SCOPES: ConfigurationScope[] = [
  { key: 'billing', label: 'Billing Configuration', description: 'Activities, POS codes, diagnosis codes, dependencies', selected: true },
  { key: 'member_profile', label: 'Member Profile', description: 'Field categories, custom fields, configurations', selected: true },
  { key: 'programs', label: 'Programs', description: 'Program definitions and field visibility', selected: true },
  { key: 'roles', label: 'Organization Roles', description: 'Role terminology, program access, permissions', selected: false },
  { key: 'workflows', label: 'Workflows', description: 'Workflow templates and forms', selected: false },
];

// Use direct backend API URLs to avoid Vercel routing issues
// Frontend URLs (dev.weavein.ai) get routed through Vercel which causes auth issues
const DEFAULT_ENVIRONMENTS: Environment[] = [
  { key: 'dev', name: 'Development', url: 'https://trust-marketplace-alov.onrender.com', connected: false },
  { key: 'production', name: 'Production', url: 'https://weavein-api.onrender.com', connected: false },
];

const MERGE_STRATEGIES = [
  { value: 'replace', label: 'Replace', description: 'Update existing records with new values' },
  { value: 'merge', label: 'Merge', description: 'Only update non-null fields' },
  { value: 'skip', label: 'Skip Existing', description: 'Only create new records' },
];

interface ConfigMigrationPageProps {
  onNavigate?: (tab: string) => void;
}

export default function ConfigMigrationPage({ onNavigate }: ConfigMigrationPageProps) {
  // State
  const [activeTab, setActiveTab] = useState<'export' | 'import' | 'transfer'>('export');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Export state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [scopes, setScopes] = useState<ConfigurationScope[]>(DEFAULT_SCOPES);
  const [exportPackage, setExportPackage] = useState<ExportPackage | null>(null);

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPackage, setImportPackage] = useState<ExportPackage | null>(null);
  const [targetOrgId, setTargetOrgId] = useState<number | null>(null);
  const [mergeStrategy, setMergeStrategy] = useState('replace');
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);

  // Transfer state
  const [environments, setEnvironments] = useState<Environment[]>(DEFAULT_ENVIRONMENTS);
  const [sourceEnv, setSourceEnv] = useState<string>('dev');
  const [targetEnv, setTargetEnv] = useState<string>('production');
  const [sourceOrgId, setSourceOrgId] = useState<number | null>(null);
  const [targetOrgIdTransfer, setTargetOrgIdTransfer] = useState<number | null>(null);
  const [sourceOrgs, setSourceOrgs] = useState<Organization[]>([]);
  const [targetOrgs, setTargetOrgs] = useState<Organization[]>([]);

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id') || '';
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      'X-CBO-ID': cboId,
    };
  };

  // Load organizations on mount
  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/cbos'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
        if (data.length > 0 && !selectedOrgId) {
          setSelectedOrgId(data[0].id);
          setTargetOrgId(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  // Generate API token for cross-environment transfer
  const handleGenerateToken = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getWeaveinApiUrl('/api/auth/generate-api-token'), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          days: 30,
          purpose: 'config_migration'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate token');
      }

      const data = await response.json();
      setGeneratedToken(data.token);
      setShowTokenModal(true);
      setSuccess(`Token generated successfully. Valid until ${new Date(data.expires_at).toLocaleDateString()}`);
    } catch (err: any) {
      setError(err.message || 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  };

  // Copy token to clipboard
  const copyTokenToClipboard = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  // Toggle scope selection
  const toggleScope = (key: string) => {
    setScopes(prev => prev.map(s =>
      s.key === key ? { ...s, selected: !s.selected } : s
    ));
  };

  // Export configuration
  const handleExport = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization');
      return;
    }

    const selectedScopes = scopes.filter(s => s.selected).map(s => s.key);
    if (selectedScopes.length === 0) {
      setError('Please select at least one configuration scope');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getWeaveinApiUrl('/api/config/export'), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          organization_id: selectedOrgId,
          include: selectedScopes,
          format: 'json'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setExportPackage(data);
        setSuccess('Configuration exported successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Export failed');
      }
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  // Download export as file
  const downloadExport = () => {
    if (!exportPackage) return;

    const blob = new Blob([JSON.stringify(exportPackage, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `config_${exportPackage.metadata.organization_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Copy export to clipboard
  const copyToClipboard = async () => {
    if (!exportPackage) return;
    await navigator.clipboard.writeText(JSON.stringify(exportPackage, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target?.result as string);
          setImportPackage(data);
          setError(null);
        } catch (err) {
          setError('Invalid JSON file');
          setImportPackage(null);
        }
      };
      reader.readAsText(file);
    }
  };

  // Preview import
  const handlePreviewImport = async () => {
    if (!importPackage || !targetOrgId) {
      setError('Please select a file and target organization');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getWeaveinApiUrl('/api/config/import/preview'), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          export_package: importPackage,
          target_organization_id: targetOrgId,
          merge_strategy: mergeStrategy
        })
      });

      if (response.ok) {
        const data = await response.json();
        setImportPreview(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Preview failed');
      }
    } catch (err: any) {
      setError(err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  // Execute import
  const handleExecuteImport = async () => {
    if (!importPreview) {
      setError('Please preview the import first');
      return;
    }

    if (!importPreview.ready_to_import) {
      setError('Cannot import: Please resolve errors first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getWeaveinApiUrl('/api/config/import/execute'), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          preview_id: importPreview.preview_id,
          confirmed: true,
          create_rollback: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Import completed: ${data.status}`);
        setImportPreview(null);
        setImportPackage(null);
        setImportFile(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Import failed');
      }
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  // Connect to environment
  const connectToEnvironment = async (envKey: string, token: string) => {
    const env = environments.find(e => e.key === envKey);
    if (!env) return;

    setLoading(true);
    try {
      // Test connection by fetching organizations
      const response = await fetch(`${env.url}/api/cbos`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const orgs = await response.json();
        setEnvironments(prev => prev.map(e =>
          e.key === envKey ? { ...e, connected: true, token } : e
        ));

        if (envKey === sourceEnv) {
          setSourceOrgs(orgs);
        } else if (envKey === targetEnv) {
          setTargetOrgs(orgs);
        }

        setSuccess(`Connected to ${env.name}`);
      } else {
        setError(`Failed to connect to ${env.name}: Invalid credentials`);
      }
    } catch (err: any) {
      setError(`Failed to connect to ${env.name}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Start cross-environment transfer
  const handleStartTransfer = async () => {
    const source = environments.find(e => e.key === sourceEnv);
    const target = environments.find(e => e.key === targetEnv);

    if (!source?.connected || !target?.connected) {
      setError('Both source and target environments must be connected');
      return;
    }

    if (!sourceOrgId) {
      setError('Please select a source organization');
      return;
    }

    if (!targetOrgIdTransfer) {
      setError('Please select a target organization');
      return;
    }

    const selectedScopes = scopes.filter(s => s.selected).map(s => s.key);
    if (selectedScopes.length === 0) {
      setError('Please select at least one configuration scope');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Step 1: Export from source environment
      setSuccess('Step 1/3: Exporting configuration from source...');

      const exportResponse = await fetch(`${source.url}/api/config/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${source.token}`,
          'X-CBO-ID': String(sourceOrgId),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          organization_id: sourceOrgId,
          include: selectedScopes,
          format: 'json'
        })
      });

      if (!exportResponse.ok) {
        const errorData = await exportResponse.json();
        throw new Error(`Export failed: ${errorData.detail || 'Unknown error'}`);
      }

      const exportData = await exportResponse.json();

      // Step 2: Preview import on target environment
      setSuccess('Step 2/3: Previewing import on target...');

      const previewResponse = await fetch(`${target.url}/api/config/import/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${target.token}`,
          'X-CBO-ID': String(targetOrgIdTransfer),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          export_package: exportData,
          target_organization_id: targetOrgIdTransfer,
          merge_strategy: mergeStrategy
        })
      });

      if (!previewResponse.ok) {
        const errorData = await previewResponse.json();
        throw new Error(`Preview failed: ${errorData.detail || 'Unknown error'}`);
      }

      const previewData = await previewResponse.json();

      if (!previewData.ready_to_import) {
        setImportPreview(previewData);
        throw new Error('Import has errors that must be resolved. Check preview below.');
      }

      // Step 3: Execute import on target environment
      setSuccess('Step 3/3: Executing import on target...');

      const executeResponse = await fetch(`${target.url}/api/config/import/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${target.token}`,
          'X-CBO-ID': String(targetOrgIdTransfer),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preview_id: previewData.preview_id,
          confirmed: true,
          create_rollback: true
        })
      });

      if (!executeResponse.ok) {
        const errorData = await executeResponse.json();
        throw new Error(`Import failed: ${errorData.detail || 'Unknown error'}`);
      }

      const result = await executeResponse.json();

      setImportPreview(previewData);
      setSuccess(`Transfer completed successfully! Status: ${result.status}. Records processed: ${
        (previewData.billing_activities?.create || 0) + (previewData.billing_activities?.update || 0) +
        (previewData.member_profile?.create || 0) + (previewData.member_profile?.update || 0) +
        (previewData.programs?.create || 0) + (previewData.programs?.update || 0) +
        (previewData.roles?.create || 0) + (previewData.roles?.update || 0)
      }`);

    } catch (err: any) {
      setError(err.message || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  // Render import counts
  const renderCounts = (label: string, counts?: ImportCounts) => {
    if (!counts) return null;
    const total = counts.create + counts.update + counts.skip + counts.error;
    if (total === 0) return null;

    return (
      <div className="flex items-center justify-between py-2 border-b">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex gap-2">
          {counts.create > 0 && (
            <Badge variant="outline" className="bg-green-50 text-green-700">
              +{counts.create} new
            </Badge>
          )}
          {counts.update > 0 && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {counts.update} update
            </Badge>
          )}
          {counts.skip > 0 && (
            <Badge variant="outline" className="bg-paper text-ink-500">
              {counts.skip} skip
            </Badge>
          )}
          {counts.error > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700">
              {counts.error} error
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Developer"
        title="Configuration Migration"
        description="Export and import organization configuration across environments"
      />
      <PageContainer>
      {/* Alerts */}
      {error && (
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <p className="flex-1">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {success && (
        <Card className="p-4 mb-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="h-5 w-5" />
            <p className="flex-1">{success}</p>
            <Button variant="ghost" size="sm" onClick={() => setSuccess(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'export' ? 'default' : 'outline'}
          onClick={() => setActiveTab('export')}
        >
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
        <Button
          variant={activeTab === 'import' ? 'default' : 'outline'}
          onClick={() => setActiveTab('import')}
        >
          <Upload className="h-4 w-4 mr-2" />
          Import
        </Button>
        <Button
          variant={activeTab === 'transfer' ? 'default' : 'outline'}
          onClick={() => setActiveTab('transfer')}
        >
          <ArrowRight className="h-4 w-4 mr-2" />
          Cross-Environment Transfer
        </Button>
      </div>

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Export Form */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Download className="h-5 w-5" />
              Export Configuration
            </h2>

            <div className="space-y-4">
              {/* Organization Selection */}
              <div>
                <Label htmlFor="export-org">Source Organization</Label>
                <select
                  id="export-org"
                  className="w-full p-2 border rounded-md mt-1"
                  value={selectedOrgId || ''}
                  onChange={e => setSelectedOrgId(parseInt(e.target.value))}
                >
                  <option value="">Select organization...</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Scope Selection */}
              <div>
                <Label>Configuration Scopes</Label>
                <div className="mt-2 space-y-2">
                  {scopes.map(scope => (
                    <label
                      key={scope.key}
                      className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                        scope.selected ? 'bg-blue-50 border-blue-300' : 'hover:bg-paper'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={scope.selected}
                        onChange={() => toggleScope(scope.key)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">{scope.label}</div>
                        <div className="text-sm text-ink-500">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Export Button */}
              <Button
                className="w-full"
                onClick={handleExport}
                disabled={loading || !selectedOrgId}
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Export Configuration
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Export Result */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileJson className="h-5 w-5" />
              Export Result
            </h2>

            {exportPackage ? (
              <div className="space-y-4">
                {/* Metadata */}
                <div className="bg-paper p-4 rounded-md">
                  <div className="text-sm space-y-1">
                    <div><strong>Organization:</strong> {exportPackage.metadata.organization_name}</div>
                    <div><strong>Environment:</strong> {exportPackage.metadata.source_environment}</div>
                    <div><strong>Exported:</strong> {new Date(exportPackage.metadata.created_at).toLocaleString()}</div>
                    <div><strong>Scopes:</strong> {exportPackage.metadata.export_scope.join(', ')}</div>
                  </div>
                </div>

                {/* Content Summary */}
                <div className="border rounded-md divide-y">
                  {exportPackage.billing && (
                    <div className="p-3">
                      <div className="font-medium">Billing Configuration</div>
                      <div className="text-sm text-ink-500">
                        {exportPackage.billing.activities?.length || 0} activities,
                        {exportPackage.billing.place_of_service?.length || 0} POS codes,
                        {exportPackage.billing.diagnosis_codes?.length || 0} diagnosis links
                      </div>
                    </div>
                  )}
                  {exportPackage.member_profile && (
                    <div className="p-3">
                      <div className="font-medium">Member Profile</div>
                      <div className="text-sm text-ink-500">
                        {exportPackage.member_profile.categories?.length || 0} categories,
                        {exportPackage.member_profile.custom_fields?.length || 0} custom fields,
                        {exportPackage.member_profile.configurations?.length || 0} configurations
                      </div>
                    </div>
                  )}
                  {exportPackage.programs && (
                    <div className="p-3">
                      <div className="font-medium">Programs</div>
                      <div className="text-sm text-ink-500">
                        {exportPackage.programs.programs?.length || 0} programs
                      </div>
                    </div>
                  )}
                  {exportPackage.roles && (
                    <div className="p-3">
                      <div className="font-medium">Roles</div>
                      <div className="text-sm text-ink-500">
                        {exportPackage.roles.roles?.length || 0} roles
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button onClick={downloadExport} className="flex-1">
                    <Download className="h-4 w-4 mr-2" />
                    Download JSON
                  </Button>
                  <Button variant="outline" onClick={copyToClipboard}>
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-ink-500">
                <FileJson className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Export configuration to see results here</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Import Form */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Import Configuration
            </h2>

            <div className="space-y-4">
              {/* File Selection */}
              <div>
                <Label>Configuration File</Label>
                <div
                  className={`mt-2 border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
                    importFile ? 'border-green-300 bg-green-50' : 'border-v2border hover:border-blue-300'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-700">
                      <CheckCircle2 className="h-5 w-5" />
                      <span>{importFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-ink-500">
                      <Upload className="h-8 w-8 mx-auto mb-2" />
                      <p>Click to select or drag and drop</p>
                      <p className="text-sm">JSON configuration file</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Package Info */}
              {importPackage && (
                <div className="bg-blue-50 p-4 rounded-md">
                  <div className="text-sm space-y-1">
                    <div><strong>From:</strong> {importPackage.metadata.organization_name}</div>
                    <div><strong>Environment:</strong> {importPackage.metadata.source_environment}</div>
                    <div><strong>Created:</strong> {new Date(importPackage.metadata.created_at).toLocaleString()}</div>
                  </div>
                </div>
              )}

              {/* Target Organization */}
              <div>
                <Label htmlFor="import-org">Target Organization</Label>
                <select
                  id="import-org"
                  className="w-full p-2 border rounded-md mt-1"
                  value={targetOrgId || ''}
                  onChange={e => setTargetOrgId(parseInt(e.target.value))}
                >
                  <option value="">Select organization...</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Merge Strategy */}
              <div>
                <Label>Merge Strategy</Label>
                <div className="mt-2 space-y-2">
                  {MERGE_STRATEGIES.map(strategy => (
                    <label
                      key={strategy.value}
                      className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                        mergeStrategy === strategy.value ? 'bg-blue-50 border-blue-300' : 'hover:bg-paper'
                      }`}
                    >
                      <input
                        type="radio"
                        name="merge-strategy"
                        value={strategy.value}
                        checked={mergeStrategy === strategy.value}
                        onChange={e => setMergeStrategy(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">{strategy.label}</div>
                        <div className="text-sm text-ink-500">{strategy.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preview Button */}
              <Button
                className="w-full"
                variant="outline"
                onClick={handlePreviewImport}
                disabled={loading || !importPackage || !targetOrgId}
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating Preview...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Import
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Import Preview */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Import Preview
            </h2>

            {importPreview ? (
              <div className="space-y-4">
                {/* Status */}
                <div className={`p-4 rounded-md ${
                  importPreview.ready_to_import ? 'bg-green-50' : 'bg-red-50'
                }`}>
                  <div className="flex items-center gap-2">
                    {importPreview.ready_to_import ? (
                      <>
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <span className="text-green-800 font-medium">Ready to import</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        <span className="text-red-800 font-medium">Cannot import - resolve errors</span>
                      </>
                    )}
                  </div>
                  <div className="text-sm mt-1 text-ink-500">
                    Target: {importPreview.target_organization_name}
                  </div>
                </div>

                {/* Changes Summary */}
                <div className="border rounded-md">
                  {renderCounts('Billing Activities', importPreview.billing_activities)}
                  {renderCounts('Place of Service', importPreview.place_of_service)}
                  {renderCounts('Diagnosis Codes', importPreview.diagnosis_codes)}
                  {renderCounts('Dependencies', importPreview.billing_dependencies)}
                  {renderCounts('Field Categories', importPreview.field_categories)}
                  {renderCounts('Custom Fields', importPreview.custom_fields)}
                  {renderCounts('Field Configurations', importPreview.field_configurations)}
                  {renderCounts('Programs', importPreview.programs)}
                  {renderCounts('Roles', importPreview.roles)}
                </div>

                {/* Warnings */}
                {importPreview.warnings.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-md">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      <span className="font-medium text-yellow-800">
                        {importPreview.warnings.length} Warning(s)
                      </span>
                    </div>
                    <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                      {importPreview.warnings.slice(0, 5).map((w, i) => (
                        <li key={i}>{w.message}</li>
                      ))}
                      {importPreview.warnings.length > 5 && (
                        <li>...and {importPreview.warnings.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Errors */}
                {importPreview.errors.length > 0 && (
                  <div className="bg-red-50 p-4 rounded-md">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <span className="font-medium text-red-800">
                        {importPreview.errors.length} Error(s)
                      </span>
                    </div>
                    <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                      {importPreview.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Execute Button */}
                <Button
                  className="w-full"
                  onClick={handleExecuteImport}
                  disabled={loading || !importPreview.ready_to_import}
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Execute Import
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center py-12 text-ink-500">
                <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Preview import to see changes here</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Transfer Tab */}
      {activeTab === 'transfer' && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ArrowRight className="h-5 w-5" />
            Cross-Environment Transfer
          </h2>

          <div className="bg-yellow-50 p-4 rounded-md mb-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <div className="font-medium text-yellow-800">Security Notice</div>
                  <p className="text-sm text-yellow-700">
                    Cross-environment transfer requires authentication tokens for both source and target environments.
                    Tokens are stored in memory only and are not persisted.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateToken}
                disabled={loading}
                className="whitespace-nowrap"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Settings className="h-4 w-4 mr-1" />
                    Generate Token
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Token Modal */}
          {showTokenModal && generatedToken && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="w-full max-w-2xl mx-4 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    API Token Generated
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowTokenModal(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="bg-paper-2 p-4 rounded-md mb-4">
                  <p className="text-xs text-ink-500 mb-2">Your API token (valid for 30 days):</p>
                  <div className="bg-surface p-3 rounded border font-mono text-xs break-all max-h-32 overflow-y-auto">
                    {generatedToken}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={copyTokenToClipboard} className="flex-1">
                    {tokenCopied ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Token
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setShowTokenModal(false)}>
                    Close
                  </Button>
                </div>

                <p className="text-xs text-ink-500 mt-4">
                  Use this token in the API Token field for the current environment.
                  For cross-environment transfer, generate tokens on each environment separately.
                </p>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Source Environment */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Server className="h-4 w-4" />
                Source Environment
              </h3>

              <select
                className="w-full p-2 border rounded-md"
                value={sourceEnv}
                onChange={e => setSourceEnv(e.target.value)}
              >
                {environments.map(env => (
                  <option key={env.key} value={env.key}>
                    {env.name} ({env.url})
                  </option>
                ))}
              </select>

              {!environments.find(e => e.key === sourceEnv)?.connected ? (
                <div className="space-y-2">
                  <Label>API Token</Label>
                  <Input
                    type="password"
                    placeholder="Enter API token..."
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        connectToEnvironment(sourceEnv, (e.target as HTMLInputElement).value);
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const input = document.querySelector('input[type="password"]') as HTMLInputElement;
                      if (input?.value) {
                        connectToEnvironment(sourceEnv, input.value);
                      }
                    }}
                  >
                    Connect
                  </Button>
                </div>
              ) : (
                <div className="bg-green-50 p-3 rounded-md">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    Connected
                  </div>
                </div>
              )}

              {sourceOrgs.length > 0 && (
                <div>
                  <Label>Source Organization</Label>
                  <select
                    className="w-full p-2 border rounded-md mt-1"
                    value={sourceOrgId || ''}
                    onChange={e => setSourceOrgId(parseInt(e.target.value))}
                  >
                    <option value="">Select...</option>
                    {sourceOrgs.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center">
              <ArrowRight className="h-8 w-8 text-ink-400" />
            </div>

            {/* Target Environment */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Server className="h-4 w-4" />
                Target Environment
              </h3>

              <select
                className="w-full p-2 border rounded-md"
                value={targetEnv}
                onChange={e => setTargetEnv(e.target.value)}
              >
                {environments.filter(e => e.key !== sourceEnv).map(env => (
                  <option key={env.key} value={env.key}>
                    {env.name} ({env.url})
                  </option>
                ))}
              </select>

              {!environments.find(e => e.key === targetEnv)?.connected ? (
                <div className="space-y-2">
                  <Label>API Token</Label>
                  <Input
                    type="password"
                    placeholder="Enter API token..."
                    id="target-token"
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const input = document.getElementById('target-token') as HTMLInputElement;
                      if (input?.value) {
                        connectToEnvironment(targetEnv, input.value);
                      }
                    }}
                  >
                    Connect
                  </Button>
                </div>
              ) : (
                <div className="bg-green-50 p-3 rounded-md">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    Connected
                  </div>
                </div>
              )}

              {targetOrgs.length > 0 && (
                <div>
                  <Label>Target Organization</Label>
                  <select
                    className="w-full p-2 border rounded-md mt-1"
                    value={targetOrgIdTransfer || ''}
                    onChange={e => setTargetOrgIdTransfer(parseInt(e.target.value))}
                  >
                    <option value="">Select...</option>
                    {targetOrgs.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Transfer Actions */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex gap-4">
              <Button
                onClick={handleStartTransfer}
                disabled={loading || !sourceOrgId || !targetOrgIdTransfer || !environments.find(e => e.key === sourceEnv)?.connected || !environments.find(e => e.key === targetEnv)?.connected}
                className="flex-1"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Transferring...' : 'Start Transfer'}
              </Button>
            </div>
            <p className="text-sm text-ink-500 mt-2 text-center">
              This will export from source and import to target in one operation
            </p>
          </div>
        </Card>
      )}
      </PageContainer>
    </div>
  );
}
